import React from 'react';
import { useAttendance } from '../../hooks/useAttendance';

/**
 * Global Clock Button Component
 * Highly aesthetic, session-aware, and synchronized via Redux.
 *
 * Shift-aware: an employee can have up to two rostered shifts a day. This
 * reads the same per-slot state (`slots`/`activeSlot`/`nextSlot`/
 * `allShiftsDone`) ESS-Mobile's clock-in screen already uses, so clocking in
 * for a second shift is visibly labeled "Shift 2" instead of looking
 * identical to the first, and the button disables once both are done instead
 * of silently re-using shift 1 forever.
 */
const ClockButton = () => {
    const { isClockedIn, activeSlot, nextSlot, allShiftsDone, handleToggle, isLoading } = useAttendance();

    const activeShiftLabel = activeSlot?.slot === 2 ? 'Shift 2' : activeSlot?.shiftName || null;
    const nextShiftLabel = nextSlot?.slot === 2 ? 'Shift 2' : null;

    if (!isLoading && allShiftsDone && !isClockedIn) {
        return (
            <div
                className="relative flex items-center gap-2 px-5 py-2.5 rounded-2xl font-black text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-600 border-2 border-emerald-100"
                title="All of today's scheduled shifts have been completed"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                <span>Shifts Completed</span>
            </div>
        );
    }

    return (
        <button
            onClick={handleToggle}
            disabled={isLoading}
            className={`
                relative flex items-center gap-3 px-5 py-2.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all duration-500
                ${isClockedIn
                    ? 'bg-rose-50 text-rose-600 border-2 border-rose-100 hover:bg-rose-100 shadow-lg shadow-rose-100/50'
                    : 'bg-indigo-600 text-white border-2 border-indigo-500 hover:bg-indigo-700 shadow-xl shadow-indigo-200/50'
                }
                ${isLoading ? 'opacity-50 cursor-not-allowed scale-95' : 'hover:-translate-y-0.5 active:scale-95'}
            `}
        >
            {isLoading ? (
                <div className="flex items-center gap-2">
                    <svg className="animate-spin h-3 w-3 text-current" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Syncing</span>
                </div>
            ) : (
                <>
                    <div className="relative">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3" />
                            <circle cx="12" cy="12" r="9" strokeWidth={2} />
                        </svg>
                        {!isClockedIn && (
                            <span className="absolute -top-1 -right-1 flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                            </span>
                        )}
                    </div>
                    <span>
                        {isClockedIn ? 'Clock Out' : 'Clock In'}
                        {isClockedIn && activeShiftLabel && ` — ${activeShiftLabel}`}
                        {!isClockedIn && nextShiftLabel && ` — ${nextShiftLabel}`}
                    </span>

                    {/* Timestamp Pill */}
                    {isClockedIn && (
                        <div className="ml-1 pl-3 border-l border-rose-200">
                             <span className="text-[9px] font-bold opacity-80">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                    )}
                </>
            )}
        </button>
    );
};

export default ClockButton;
